/**
 * Map a failed auth request to human, branded copy for a form's error banner.
 *
 * Never surfaces a raw status code, request URL, HTTP method, or the
 * transport's verbatim string. ky's default HTTPError message is
 * "Request failed with status code N: POST https://api…", which leaks the API
 * host, the method, and the status — none of which should ever reach a user.
 * Mirrors the driver app's humanLoginError (apps/web LoginPage): branch on the
 * ky error NAME so no ky import is needed here — HTTPError carries
 * `.response.status`; TimeoutError / TypeError are the network cases.
 *
 * Auth failures (401/403 and any other unexpected 4xx) all collapse to the
 * caller's `authFailureCopy` line. Distinguishing "wrong password" from
 * "no such account" (or "not an admin") would reveal whether the address
 * exists, enabling account enumeration; mapping on the HTTP status rather than
 * the server's message keeps that guarantee even if the backend copy changes.
 *
 * The rate-limit / server-error / network buckets are identical across forms;
 * only the auth-4xx line differs (a sign-in says the credentials didn't match,
 * a magic-link request says it couldn't send), so the caller passes it in.
 */
export function humanAuthError(err: unknown, authFailureCopy: string): string {
  const name = err instanceof Error ? err.name : '';
  if (name === 'HTTPError') {
    const status = Number((err as { response?: { status?: number } }).response?.status) || 0;
    if (status === 429) return 'Too many attempts. Wait a minute and try again.';
    if (status >= 500) return 'Something went wrong on our end. Try again.';
    // 401 / 403 / any other unexpected 4xx → one neutral line, so nothing
    // leaks which half of the credential was wrong (or that it was valid but
    // lacks admin access).
    return authFailureCopy;
  }
  if (name === 'TimeoutError' || name === 'TypeError') {
    return "Couldn't reach the server. Check your connection and try again.";
  }
  return 'Something went wrong on our end. Try again.';
}

/**
 * The Admin password sign-in answers whose own words a person should read. Each comes only AFTER the password
 * matched, so it reveals nothing about whether an address exists: an invite waiting on a mailbox nobody has proved
 * yet (INVITE_CONFIRM_EMAIL, review of 0b88e4cc finding 1) says to use an emailed link or Google instead. Every
 * other failure keeps humanAuthError's one neutral line.
 */
export const LOGIN_CODES_SHOWN: ReadonlySet<string> = new Set(['INVITE_CONFIRM_EMAIL']);

export async function loginErrorCopy(err: unknown, authFailureCopy: string): Promise<string> {
  const response = (err as { response?: Response }).response;
  if (err instanceof Error && err.name === 'HTTPError' && response) {
    try {
      const body = await response.clone().json() as { error?: { code?: unknown; message?: unknown } } | null;
      const code = body?.error?.code, message = body?.error?.message;
      if (typeof code === 'string' && LOGIN_CODES_SHOWN.has(code) && typeof message === 'string' && message) return message;
    } catch { /* not JSON: the neutral line below */ }
  }
  return humanAuthError(err, authFailureCopy);
}
